// @ts-check

import { SIDE_CART_RENDERED_EVENT, updateCartDiscount } from '@theme/lib-cart';

/** @type {WeakMap<HTMLElement, AbortController>} */
const discountControllers = new WeakMap();

/**
 * @param {HTMLElement} root
 */
function hideDiscountError(root) {
  var errorContainer = root.querySelector('.side-cart__discount-error');
  var errorText = root.querySelector('.side-cart__discount-error-text');

  if (errorContainer instanceof HTMLElement) {
    errorContainer.hidden = true;
  }
  if (errorText instanceof HTMLElement) {
    errorText.textContent = '';
  }
}

/**
 * @param {HTMLElement} root
 * @param {string} message
 */
function showDiscountError(root, message) {
  var errorContainer = root.querySelector('.side-cart__discount-error');
  var errorText = root.querySelector('.side-cart__discount-error-text');

  if (errorText instanceof HTMLElement) {
    errorText.textContent = message;
  }
  if (errorContainer instanceof HTMLElement) {
    errorContainer.hidden = false;
  }
}

/**
 * @param {HTMLElement} root
 * @returns {string[]}
 */
function getAppliedDiscountCodes(root) {
  return Array.from(root.querySelectorAll('[data-discount-code]'))
    .map(function (button) {
      if (!(button instanceof HTMLElement)) return '';
      return button.dataset.discountCode || '';
    })
    .filter(function (code) {
      return code.length > 0;
    });
}

/**
 * @param {HTMLElement} root
 * @param {string[]} codes
 * @param {AbortSignal} signal
 */
async function applyDiscountCodes(root, codes, signal) {
  var applyButton = root.querySelector('.side-cart__discount-apply');
  var input = root.querySelector('.side-cart__discount-input');

  hideDiscountError(root);
  root.setAttribute('aria-busy', 'true');

  if (applyButton instanceof HTMLButtonElement) {
    applyButton.disabled = true;
  }
  if (input instanceof HTMLInputElement) {
    input.disabled = true;
  }

  try {
    await updateCartDiscount(codes, { signal });
    if (input instanceof HTMLInputElement) {
      input.value = '';
    }
  } finally {
    root.removeAttribute('aria-busy');
    if (applyButton instanceof HTMLButtonElement) {
      applyButton.disabled = false;
    }
    if (input instanceof HTMLInputElement) {
      input.disabled = false;
    }
  }
}

/**
 * @param {HTMLElement} root
 */
function initSideCartDiscount(root) {
  var existingController = discountControllers.get(root);
  existingController?.abort();

  var controller = new AbortController();
  discountControllers.set(root, controller);

  var form = root.querySelector('.side-cart__discount-form');
  var input = root.querySelector('.side-cart__discount-input');

  if (!(form instanceof HTMLFormElement)) return;

  form.addEventListener(
    'submit',
    async function (event) {
      event.preventDefault();

      if (!(input instanceof HTMLInputElement)) return;

      var code = input.value.trim();
      if (!code) return;

      var existingCodes = getAppliedDiscountCodes(root);
      var alreadyApplied = existingCodes.some(function (existingCode) {
        return existingCode.toUpperCase() === code.toUpperCase();
      });

      if (alreadyApplied) {
        showDiscountError(
          root,
          Theme.translations.discount_already_applied ||
            'This discount code is already applied.'
        );
        return;
      }

      try {
        await applyDiscountCodes(
          root,
          existingCodes.concat(code),
          controller.signal
        );
      } catch (error) {
        var message =
          error instanceof Error
            ? error.message
            : Theme.translations.discount_error ||
              'Could not apply discount code.';
        showDiscountError(root, message);
      }
    },
    { signal: controller.signal }
  );

  root.addEventListener(
    'click',
    async function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;

      var removeButton = target.closest('.side-cart__discount-remove');
      if (!(removeButton instanceof HTMLElement)) return;

      var codeToRemove = removeButton.dataset.discountCode;
      if (!codeToRemove) return;

      var codeToRemoveUpper = codeToRemove.toUpperCase();
      var remainingCodes = getAppliedDiscountCodes(root).filter(
        function (code) {
          return code.toUpperCase() !== codeToRemoveUpper;
        }
      );

      try {
        await applyDiscountCodes(root, remainingCodes, controller.signal);
      } catch (error) {
        var message =
          error instanceof Error
            ? error.message
            : Theme.translations.discount_error ||
              'Could not apply discount code.';
        showDiscountError(root, message);
      }
    },
    { signal: controller.signal }
  );
}

function initAllSideCartDiscounts() {
  document
    .querySelectorAll('.side-cart__discount-code')
    .forEach(function (root) {
      if (root instanceof HTMLElement) {
        initSideCartDiscount(root);
      }
    });
}

initAllSideCartDiscounts();
document.addEventListener(SIDE_CART_RENDERED_EVENT, initAllSideCartDiscounts);
